import React from "react";
import { View, StyleSheet } from "react-native";
import NtCaseStudySectionContainer from "./NtCaseStudySectionContainer";
import NtCaseStudyText from "./NtCaseStudyText";
import NtCaseStudyTextBold from "./NtCaseStudyTextBold";
import NtCaseStudyTextBullet from "./NtCaseStudyTextBullet";

function NtCaseStudyFourSeasonsYachts({}) {
  return (
    <NtCaseStudySectionContainer
      title={
        "Case Study: Elevating Discreet Service and Guest Intimacy Aboard Four Seasons Yachts’ Four Seasons I with Voxcraft"
      }
      subtitle={
        "Four Seasons Yachts’ inaugural vessel, Four Seasons I, redefines what ultra-luxury looks like at sea — a residential, all-suite superyacht where service is personal, anticipatory, and invisible. To deliver that standard, the crew needed a communication platform as refined and unobtrusive as the experience itself. Voxcraft provided it."
      }
      image={require("../../../assets/images/partners/logo_four-seasons-yachts.png")}
    >
      <NtCaseStudyText>
        <NtCaseStudyTextBold>Overview:</NtCaseStudyTextBold> Four Seasons Yachts
        — the maritime extension of Four Seasons Hotels and Resorts, operated by
        Marc-Henry Cruise Holdings — sought a communication and connectivity
        foundation for Four Seasons I that matched the brand’s legendary
        standard of intuitive, personalized hospitality. With only 95 suites, a
        maximum of roughly 180 guests, and an industry-leading one-to-one
        staff-to-guest ratio, the vessel demanded a solution built not for mass
        coordination but for discretion, immediacy, and seamless guest care.
        Four Seasons Yachts partnered with Voxcraft, a leader in communication
        intelligence and maritime IT solutions, to deploy a purpose-built
        platform across the yacht’s service, bridge, and guest-facing
        operations.
      </NtCaseStudyText>

      <NtCaseStudyText style={{ marginTop: 20 }}>
        <NtCaseStudyTextBold>Challenge:</NtCaseStudyTextBold> Four Seasons I
        operates on a different premise than a conventional cruise ship. At
        34,000 gross tons and 207 meters, the yacht carries fewer than 180
        guests across 14 decks — meaning every guest interaction is high-touch,
        and every service request is expected to be fulfilled quickly, quietly,
        and flawlessly. A butler, concierge, or suite attendant cannot be
        tethered to a fixed station or a clunky handset; coordination has to
        happen on the move and out of sight.{"\n\n"}
        Private & Confidential{"\n\n"}
        Four Seasons Yachts identified several requirements that standard
        maritime communication tools could not satisfy:
      </NtCaseStudyText>

      <NtCaseStudyTextBullet containerStyle={{ marginTop: 20 }}>
        <NtCaseStudyTextBold>Discreet, instant coordination:</NtCaseStudyTextBold>{" "}
        With a one-to-one staff ratio and guests spread across expansive suites
        — including the 457 m² multi-level Funnel Suite — crew needed to
        coordinate butler service, dining, housekeeping, and excursions in real
        time without intrusive radio chatter in guest-facing spaces.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          Reliability independent of satellite connectivity:
        </NtCaseStudyTextBold>{" "}
        Itineraries spanning the Caribbean and Mediterranean, including smaller
        and more remote ports, meant onboard communication could not depend on
        internet or satellite uptime.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          Power and distraction management:
        </NtCaseStudyTextBold>{" "}
        Crew devices needed to deliver mission-critical alerts while suppressing
        non-essential notifications that could distract from guest-facing
        service.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          A unified safety and operations layer:
        </NtCaseStudyTextBold>{" "}
        The platform had to integrate with the vessel’s safety monitoring and
        bridge systems so that the same devices serving guests could also serve
        as a frontline safety tool.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          Accessibility and family provisions:
        </NtCaseStudyTextBold>{" "}
        As an ultra-luxury operator serving multigenerational and
        accessibility-conscious guests, Four Seasons required specialized
        communication options for guests who are deaf or hard of hearing, and a
        simple way for families to stay connected to onboard services.
      </NtCaseStudyTextBullet>

      <NtCaseStudyText style={{ marginTop: 20 }}>
        <NtCaseStudyTextBold>Solution:</NtCaseStudyTextBold> Four Seasons Yachts
        deployed Voxcraft’ mobile communication platform, configured to mirror
        the yacht’s service hierarchy and white-glove operating model. The
        platform unifies voice calling, push-to-talk, and secure chat into a
        single role-based application, allowing the crew to coordinate tasks,
        schedules, and emergency protocols from anywhere on the vessel.{"\n\n"}
        Key features of the Voxcraft deployment include:
      </NtCaseStudyText>

      <NtCaseStudyTextBullet containerStyle={{ marginTop: 20 }}>
        <NtCaseStudyTextBold>
          Enterprise mobile devices for crew:
        </NtCaseStudyTextBold>{" "}
        Durable, discreet handsets loaded with role-specific enterprise
        applications, replacing legacy DECT handsets and consumer-grade tools.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          Local push-notification environment:
        </NtCaseStudyTextBold>{" "}
        Crew receive mission-critical calls and messages onboard without
        reliance on the internet — ensuring service continuity in any port or
        open-water setting.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          Offline-capable communication:
        </NtCaseStudyTextBold>{" "}
        Voice and messaging remain fully functional without satellite or
        internet connectivity, optimizing both performance and energy efficiency
        on extended voyages.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          Integration with the vessel’s Safety Monitoring and Control System
          (SMCS):
        </NtCaseStudyTextBold>{" "}
        The platform relays emergency alarms and surfaces recommended response
        procedures directly to the right crew members, turning everyday service
        devices into an active safety tool.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          PBX integration for guest connectivity:
        </NtCaseStudyTextBold>{" "}
        By connecting to the yacht’s PBX, the app lets crew communicate
        seamlessly with guests in their suites — supporting concierge, butler,
        and dining coordination, as well as specialized handsets with
        accessibility features for guests who are deaf or hard of hearing.
        Families can be issued devices to reach childcare and guest services
        directly, adding peace of mind without compromising privacy.
      </NtCaseStudyTextBullet>

      <NtCaseStudyText style={{ marginTop: 20 }}>
        <NtCaseStudyTextBold>Results:</NtCaseStudyTextBold> With the Voxcraft
        platform in service aboard Four Seasons I, Four Seasons Yachts achieved:
      </NtCaseStudyText>

      <NtCaseStudyTextBullet containerStyle={{ marginTop: 20 }}>
        <NtCaseStudyTextBold>
          Elevated, anticipatory service:
        </NtCaseStudyTextBold>{" "}
        Crew coordinate guest requests instantly and discreetly, sustaining the
        one-to-one service standard that defines the Four Seasons experience —
        without intrusive radio traffic in guest spaces.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          Uninterrupted operations in any port:
        </NtCaseStudyTextBold>{" "}
        Because communication does not depend on satellite or internet uptime,
        the crew operates effectively across the yacht’s Caribbean and
        Mediterranean itineraries, including smaller and more remote
        destinations.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>A stronger safety posture:</NtCaseStudyTextBold>{" "}
        Integration with the SMCS ensures the crew is immediately alerted and
        guided through the correct procedures in an emergency, contributing to
        a safer environment for guests and staff alike.
      </NtCaseStudyTextBullet>
      <NtCaseStudyTextBullet containerStyle={{ marginTop: 8 }}>
        <NtCaseStudyTextBold>
          Inclusive, family-friendly connectivity:
        </NtCaseStudyTextBold>{" "}
        Accessibility-enabled handsets and family service devices extend the
        brand’s hospitality standard to every guest, including those with
        accessibility needs and traveling families.
      </NtCaseStudyTextBullet>

      <NtCaseStudyText style={{ marginTop: 20 }}>
        <NtCaseStudyTextBold>Conclusion:</NtCaseStudyTextBold> Through its
        partnership with Voxcraft, Four Seasons Yachts has equipped Four Seasons
        I with a communication system as refined as the vessel itself — one
        that empowers the crew to deliver intuitive, invisible, world-class
        service while strengthening onboard safety. As Four Seasons extends its
        renowned hospitality to the sea, this collaboration supports the
        brand’s vision of personalized luxury at every touchpoint, proving
        that the best service technology is the kind guests never have to
        notice. Four Seasons I, which entered service in 2026 with inaugural
        voyages through the Caribbean and Mediterranean, stands as a benchmark
        for the new era of ultra-luxury yachting.{"\n\n"}
        <NtCaseStudyTextBold>About Voxcraft</NtCaseStudyTextBold>
        {"\n"}Voxcraft is a leading communication intelligence and IT solutions
        provider, specializing in the development and operation of
        mission-critical communications networks. Voxcraft additionally offers
        Low Earth Orbit satellite connectivity, location services, contact
        tracing and access controls, and cutting-edge telehealth technologies
        tailored for the global maritime industry.{"\n\n"}
        <NtCaseStudyTextBold>About Four Seasons Yachts</NtCaseStudyTextBold>
        {"\n"}Four Seasons Yachts is the maritime expression of Four Seasons
        Hotels and Resorts, extending the company’s globally recognized
        standard of luxury hospitality to the sea. Its inaugural vessel, Four
        Seasons I, was built by Fincantieri at its Ancona shipyard and operated
        by joint owner-operator Marc-Henry Cruise Holdings. The
        34,000-gross-ton, 207-meter yacht features 95 all-suite accommodations
        — each with private outdoor terraces and floor-to-ceiling ocean views
        — and offers an industry-leading one-to-one staff-to-guest ratio and
        the highest space ratio in its class. With its residential, all-suite
        design and flexible, intimate itineraries across more than 130
        destinations in its first year, Four Seasons Yachts establishes a new
        reference standard for the ultra-luxury cruising segment.{"\n\n"}
        Private & Confidential. Vessel specifications are based on publicly
        reported figures for Four Seasons I. Deployment details, features, and
        outcomes described herein are illustrative and should be verified
        against the actual scope of the Voxcraft engagement prior to external
        distribution.
      </NtCaseStudyText>
    </NtCaseStudySectionContainer>
  );
}

const styles = StyleSheet.create({
  container: {},
});

export default NtCaseStudyFourSeasonsYachts;
